import { useHead } from '@unhead/react';
import { ChevronRight, Flame, Target, TrendingUp, Zap, BookOpen, Calculator, Clock, ArrowRight, CheckCircle, Users, Award, BarChart3, Sparkles, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";
import { PAGE_SEO, SITE, buildCanonical, buildWebsiteJsonLd } from '@/lib/seo';

/* ─── Image Assets ─── */
const IMAGES = {
  hero: "https://static.lumi.new/55/5561de71ecefd6401482618bcb750b8b.jpg",
  leanMan: "https://static.lumi.new/9f/9f72830f1d2a5c23ec0a4bb81f54de6a.jpg",
  womanStrength: "https://static.lumi.new/c1/c102c9e1772d19390d288f61b935d110.jpg",
  ectomorph: "https://static.lumi.new/45/45af4f5f70c9ed87c0a2df968fff10b7.jpg",
  nutrition: "https://static.lumi.new/ab/abf5f40c42ff8c828fc59a1f30f7e82c.jpg",
  legDay: "https://static.lumi.new/a5/a5a8d088e8d760c41a992bfaf2b43d03.jpg",
  bodyweight: "https://static.lumi.new/c1/c14d05b80411c32049b4b44b86451ea8.png",
  definition: "https://static.lumi.new/b9/b91b1fead38f767f5ddb056e35a21a5b.jpg",
  transformation: "https://static.lumi.new/84/844be534ad37522e75870e1aeba903e6.png"
};

/* ─── Data ─── */
const trainingHubs = [
{
  title: "The Lean Physique",
  subtitle: "Men's Training Hub",
  description:
  "Build a lean, muscular body without excessive fat gain. Science-backed programs for the lean body men aspire to achieve.",
  image: IMAGES.leanMan,
  keywords: ["Lean body for men", "Lean muscle body"],
  icon: Target,
  accent: "#FF6B35",
  href: "/lean-physique"
},
{
  title: "Women's Strength",
  subtitle: "Female Power Hub",
  description:
  "Dispelling the myths around bulking. Empowering, evidence-based programs designed for female muscle growth and total-body strength.",
  image: IMAGES.womanStrength,
  keywords: ["Female muscle growth", "Muscle mass for women"],
  icon: Flame,
  accent: "#FFA823",
  href: "/womens-strength"
},
{
  title: "Hardgainers & Ectomorphs",
  subtitle: "Transformation Hub",
  description:
  "High-calorie nutrition strategies and heavy compound lifting programs for ectomorph muscle building. Your skinny-to-strong roadmap.",
  image: IMAGES.ectomorph,
  keywords: ["Ectomorph muscle building", "Muscle gain for skinny guys"],
  icon: TrendingUp,
  accent: "#2E86AB",
  href: "/hardgainers"
}];


const articles = [
{
  title: "The Ultimate Leg Workout for Mass",
  excerpt:
  "A complete lower-body hypertrophy program targeting quads, hamstrings, and glutes with progressive overload principles.",
  image: IMAGES.legDay,
  readTime: "8 min read",
  category: "Training"
},
{
  title: "How to Achieve a 10lb Muscle Gain in 12 Weeks",
  excerpt:
  "A structured 12-week plan combining training periodization, caloric surplus strategies, and recovery optimization.",
  image: IMAGES.transformation,
  readTime: "12 min read",
  category: "Transformation"
},
{
  title: "Bodyweight Muscle Gain Workouts for Home",
  excerpt:
  "No gym? No problem. Progressive bodyweight routines that drive real hypertrophy using time-under-tension and volume.",
  image: IMAGES.bodyweight,
  readTime: "6 min read",
  category: "Home Training"
},
{
  title: "Lean Muscle Definition: The Science of Looking Ripped",
  excerpt:
  "The interplay of body fat percentage, muscle density, and water retention. How to achieve that defined, ripped look.",
  image: IMAGES.definition,
  readTime: "10 min read",
  category: "Science"
}];


const stats = [
{ value: "50K+", label: "Active Readers", icon: Users },
{ value: "200+", label: "Free Workouts", icon: Award },
{ value: "92%", label: "See Results in 8 Weeks", icon: BarChart3 }];


/* ─── Affiliate Banner ─── */
function AffiliateBanner() {
  return (
    <section className="relative pt-24 pb-0 bg-[#0F0F1A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="https://hop.clickbank.net/?affiliate=buildingmm&vendor=provive&pid=20&tid=BBMwebsite"
          target="_blank"
          rel="noopener sponsored"
          className="group relative block overflow-hidden rounded-2xl border border-[#FF6B35]/30 bg-gradient-to-r from-[#1A0F08] via-[#0F0F1A] to-[#1A0F08] hover:border-[#FF6B35]/60 transition-all duration-300 hover:-translate-y-0.5">

          {/* Glow effects */}
          <div className="absolute -top-12 -left-12 w-48 h-48 bg-[#FF6B35]/20 rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-[#FFA823]/15 rounded-full blur-[80px] pointer-events-none" />

          {/* Animated shine sweep on hover */}
          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-4 px-5 sm:px-7 py-4 sm:py-5">
            {/* Left: Icon + Text */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FFA823] flex items-center justify-center shadow-lg shadow-[#FF6B35]/30">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#FF6B35]">
                    Recommended
                  </span>
                  <span className="text-[9px] tracking-wider uppercase text-[#9CA3AF] bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">
                    Sponsored
                  </span>
                </div>
                <p
                  className="text-white text-sm sm:text-base font-bold leading-tight"
                  style={{ fontFamily: "var(--font-heading)" }}>

                  BOOST YOUR GAINS — <span className="text-[#FFA823]">PROVEN MEN'S HEALTH FORMULA</span>
                </p>
                <p className="text-xs sm:text-sm mt-0.5 hidden sm:block text-left text-[#FFFFFF]">ProstaVive: The Most Potent,Fast-Acting Formula for Prostate Health & Strong Flow, Thousands of men of all ages are supporting healthy, youthful prostates along with strong urine flow, an active sex life and deep, satisfying sleep every night…All thanks to a simple, powerful blend of prostate-boosting nutrients. Click The Learn More Now Button!

                </p>
              </div>
            </div>

            {/* Right: CTA */}
            <div
              className="shrink-0 flex items-center gap-2 bg-gradient-to-r from-[#FF6B35] to-[#FFA823] text-white font-bold px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm tracking-wider uppercase shadow-lg shadow-[#FF6B35]/30 group-hover:shadow-[#FF6B35]/50 group-hover:scale-105 transition-all duration-300"
              style={{ fontFamily: "var(--font-heading)" }}>

              Learn More
              <ExternalLink className="w-3.5 h-3.5" />
            </div>
          </div>
        </a>
      </div>
    </section>);

}

/* ─── Mid-Page Affiliate Banner (between Hero and Training Hubs) ─── */
function MidAffiliateBanner() {
  return (
    <section className="relative py-10 sm:py-12 bg-[#0F0F1A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="https://hop.clickbank.net/?affiliate=buildingmm&vendor=spartamax&tid=&pid=112&hd=0"
          target="_blank"
          rel="noopener sponsored"
          className="group relative block overflow-hidden rounded-2xl border border-[#FF6B35]/30 bg-gradient-to-r from-[#1A0F08] via-[#0F0F1A] to-[#1A0F08] hover:border-[#FF6B35]/60 transition-all duration-300 hover:-translate-y-0.5">

          {/* Glow effects */}
          <div className="absolute -top-12 -left-12 w-48 h-48 bg-[#FF6B35]/20 rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-[#FFA823]/15 rounded-full blur-[80px] pointer-events-none" />

          {/* Animated shine sweep on hover */}
          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-4 px-5 sm:px-7 py-4 sm:py-5">
            {/* Left: Icon + Text */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FFA823] flex items-center justify-center shadow-lg shadow-[#FF6B35]/30">
                <Flame className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#FF6B35]">
                    Top Pick
                  </span>
                  <span className="text-[9px] tracking-wider uppercase text-[#9CA3AF] bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">
                    Sponsored
                  </span>
                </div>
                <p
                  className="text-white text-sm sm:text-base font-bold leading-tight"
                  style={{ fontFamily: "var(--font-heading)" }}>

                  UNLEASH PEAK PERFORMANCE — <span className="text-[#FFA823]">SPARTA MAX STRENGTH</span>
                </p>
                <p className="text-xs sm:text-sm mt-0.5 hidden sm:block text-[#FFFFFF]">Designed to help promote healthy body function and support extended energy levels, Spartamax is like nothing you've ever tried before... it's based off a closely guarded formula that's said to be able to transform your libido, your stamina and your confidence. Click The Discover Now Button!

                </p>
              </div>
            </div>

            {/* Right: CTA */}
            <div
              className="shrink-0 flex items-center gap-2 bg-gradient-to-r from-[#FF6B35] to-[#FFA823] text-white font-bold px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm tracking-wider uppercase shadow-lg shadow-[#FF6B35]/30 group-hover:shadow-[#FF6B35]/50 group-hover:scale-105 transition-all duration-300"
              style={{ fontFamily: "var(--font-heading)" }}>

              Discover Now
              <ExternalLink className="w-3.5 h-3.5" />
            </div>
          </div>
        </a>
      </div>
    </section>);

}

/* ─── Hero Section ─── */
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Athletes training in gym"
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F1A] via-[#0F0F1A]/85 to-[#0F0F1A]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F1A] via-transparent to-[#0F0F1A]/30" />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#FF6B35]/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/3 left-1/3 w-64 h-64 bg-[#2E86AB]/6 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 w-full">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="animate-fade-in inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-7">
            <Zap className="w-3.5 h-3.5 text-[#FF6B35]" />
            <span className="text-xs text-[#9CA3AF] tracking-wider uppercase">
              Evidence-Based Training
            </span>
          </div>

          {/* Headline */}
          <h1
            className="animate-fade-in-up text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] mb-6"
            style={{ fontFamily: "var(--font-heading)" }}>

            BUILD
            <br />
            <span className="gradient-text">BIGGER MUSCLES</span>
            <br />
            <span className="text-[#F5F5F5]/70 text-4xl sm:text-5xl lg:text-6xl">
              THE SMART WAY
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className="animate-fade-in-up text-lg sm:text-xl text-[#9CA3AF] leading-relaxed mb-9 max-w-lg"
            style={{ animationDelay: "200ms", animationFillMode: "forwards" }}>

            Free, professional-grade workout routines and nutrition guides to
            build <strong className="text-[#F5F5F5]">lean muscle</strong>{" "}
            — no matter your starting body type.
          </p>

          {/* CTAs */}
          <div
            className="animate-fade-in-up flex flex-col sm:flex-row gap-4"
            style={{ animationDelay: "400ms", animationFillMode: "forwards" }}>

            <button
              onClick={() =>
              document.
              querySelector("#training-hubs")?.
              scrollIntoView({ behavior: "smooth" })
              }
              className="btn-shimmer text-white font-semibold px-8 py-4 rounded-xl text-base tracking-wider uppercase flex items-center justify-center gap-2 hover:scale-105 transition-transform duration-200 animate-pulse-glow"
              style={{ fontFamily: "var(--font-heading)" }}>

              Start Training
              <ChevronRight className="w-5 h-5" />
            </button>
            <Link
              to="/calculator"
              className="bg-white/5 border border-white/15 text-[#F5F5F5] font-semibold px-8 py-4 rounded-xl text-base tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-white/10 hover:border-[#FF6B35]/30 transition-all duration-200"
              style={{ fontFamily: "var(--font-heading)" }}>

              <Calculator className="w-5 h-5 text-[#FF6B35]" />
              Muscle Calculator
            </Link>
          </div>

          {/* Quick stats */}
          <div
            className="animate-fade-in-up mt-14 flex flex-wrap gap-8"
            style={{ animationDelay: "600ms", animationFillMode: "forwards" }}>

            {stats.map(({ value, label, icon: Icon }) =>
            <div key={label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <div>
                  <div
                  className="text-[#F5F5F5] text-xl font-bold"
                  style={{ fontFamily: "var(--font-heading)" }}>

                    {value}
                  </div>
                  <div className="text-[#9CA3AF] text-xs">{label}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in opacity-60">
        <span className="text-[10px] text-[#9CA3AF] tracking-[0.3em] uppercase">
          Scroll
        </span>
        <div className="w-px h-8 bg-gradient-to-b from-[#FF6B35] to-transparent" />
      </div>
    </section>);

}

/* ─── Training Hubs Section ─── */
function TrainingHubsSection() {
  return (
    <section id="training-hubs" className="relative py-24 lg:py-32">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#FF6B35]/4 rounded-full blur-[180px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <ScrollReveal className="text-center mb-16">
          <span className="inline-block text-xs text-[#FF6B35] tracking-[0.3em] uppercase mb-3 font-semibold">
            Specialized Programs
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F5] mb-5"
            style={{ fontFamily: "var(--font-heading)" }}>

            TRAINING <span className="gradient-text">HUBS</span>
          </h2>
          <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto leading-relaxed">
            Three specialized pathways designed for your unique goals. Whether
            you want to get lean, build female strength, or transform from
            skinny to strong.
          </p>
        </ScrollReveal>

        {/* Hub cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {trainingHubs.map((hub, i) =>
          <ScrollReveal key={hub.title} delay={i * 150}>
              <Link to={hub.href} className="group relative h-full bg-[#1A1A2E]/60 border border-white/5 rounded-2xl overflow-hidden hover:border-white/15 transition-all duration-500 hover:-translate-y-1 block">
                {/* Image */}
                <div className="relative h-72 overflow-hidden">
                  <img
                  src={hub.image}
                  alt={hub.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E] via-[#1A1A2E]/30 to-transparent" />

                  {/* Icon badge */}
                  <div
                  className="absolute top-4 right-4 w-11 h-11 rounded-xl flex items-center justify-center backdrop-blur-md"
                  style={{
                    backgroundColor: `${hub.accent}20`,
                    border: `1px solid ${hub.accent}40`
                  }}>

                    <hub.icon
                    className="w-5 h-5"
                    style={{ color: hub.accent }} />

                  </div>

                  {/* Subtitle */}
                  <div className="absolute bottom-4 left-5">
                    <span
                    className="text-xs tracking-[0.2em] uppercase font-semibold"
                    style={{ color: hub.accent }}>

                      {hub.subtitle}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3
                  className="text-2xl font-bold text-[#F5F5F5] mb-3"
                  style={{ fontFamily: "var(--font-heading)" }}>

                    {hub.title}
                  </h3>
                  <p className="text-[#9CA3AF] text-sm leading-relaxed mb-5">
                    {hub.description}
                  </p>

                  {/* Keyword tags */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {hub.keywords.map((kw) =>
                  <span
                    key={kw}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[#9CA3AF]">

                        {kw}
                      </span>
                  )}
                  </div>

                  {/* CTA */}
                  <span
                  className="flex items-center gap-2 text-sm font-semibold tracking-wider uppercase group-hover:gap-3 transition-all duration-300"
                  style={{
                    color: hub.accent,
                    fontFamily: "var(--font-heading)"
                  }}>

                    Explore Program
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </ScrollReveal>
          )}
        </div>
      </div>
    </section>);

}

/* ─── Featured Articles Section ─── */
function ArticlesSection() {
  return (
    <section id="articles" className="relative py-24 lg:py-32 bg-[#0A0A16]">
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#2E86AB]/4 rounded-full blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <ScrollReveal className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-14">
          <div>
            <span className="inline-block text-xs text-[#FF6B35] tracking-[0.3em] uppercase mb-3 font-semibold">
              Knowledge Base
            </span>
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#F5F5F5]"
              style={{ fontFamily: "var(--font-heading)" }}>

              LATEST <span className="gradient-text">ARTICLES</span>
            </h2>
          </div>
          <button
            className="flex items-center gap-2 text-[#FF6B35] text-sm font-semibold tracking-wider uppercase hover:gap-3 transition-all duration-300"
            style={{ fontFamily: "var(--font-heading)" }}>

            View All Articles
            <ArrowRight className="w-4 h-4" />
          </button>
        </ScrollReveal>

        {/* Article grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {articles.map((article, i) =>
          <ScrollReveal key={article.title} delay={i * 100}>
              <article className="group relative bg-[#1A1A2E]/40 border border-white/5 rounded-2xl overflow-hidden hover:border-white/15 transition-all duration-500 hover:-translate-y-1 cursor-pointer">
                <div className="flex flex-col sm:flex-row h-full">
                  {/* Thumbnail */}
                  <div className="relative sm:w-48 h-48 sm:h-auto shrink-0 overflow-hidden">
                    <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />

                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#1A1A2E]/60 hidden sm:block" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-5 sm:p-6 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[10px] px-2.5 py-1 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] font-semibold tracking-wider uppercase">
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1 text-[#666] text-xs">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                      </span>
                    </div>
                    <h3
                    className="text-xl font-bold text-[#F5F5F5] mb-2 group-hover:text-[#FF6B35] transition-colors duration-300"
                    style={{ fontFamily: "var(--font-heading)" }}>

                      {article.title}
                    </h3>
                    <p className="text-[#9CA3AF] text-sm leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-1.5 text-[#FF6B35] text-xs font-semibold tracking-wider uppercase group-hover:gap-2.5 transition-all duration-300">
                      <BookOpen className="w-3.5 h-3.5" />
                      Read Article
                    </div>
                  </div>
                </div>
              </article>
            </ScrollReveal>
          )}
        </div>
      </div>
    </section>);

}

/* ─── Calculator CTA Section ─── */
function CalculatorCTA() {
  return (
    <section id="calculator" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/8 via-[#0F0F1A] to-[#2E86AB]/6" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#FF6B35]/5 rounded-full blur-[200px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-[#1A1A2E]/60 border border-white/10 rounded-3xl overflow-hidden">
          {/* Noise texture */}
          <div className="noise-overlay absolute inset-0 pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0">
            {/* Left content */}
            <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full px-4 py-1.5 mb-6">
                  <Calculator className="w-3.5 h-3.5 text-[#FF6B35]" />
                  <span className="text-xs text-[#FF6B35] tracking-wider uppercase font-semibold">
                    Free Tool
                  </span>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={100}>
                <h2
                  className="text-4xl sm:text-5xl font-bold text-[#F5F5F5] mb-5 leading-tight"
                  style={{ fontFamily: "var(--font-heading)" }}>

                  MUSCLE MASS
                  <br />
                  <span className="gradient-text">CALCULATOR</span>
                </h2>
              </ScrollReveal>

              <ScrollReveal delay={200}>
                <p className="text-[#9CA3AF] text-lg leading-relaxed mb-8 max-w-md">
                  Discover your ideal caloric intake, protein requirements, and
                  realistic muscle gain timeline based on your body type and
                  training experience.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={300}>
                <ul className="space-y-3 mb-9">
                  {[
                  "Personalized calorie & macro targets",
                  "Realistic muscle gain projections",
                  "Body type analysis (ecto/meso/endo)",
                  "Custom workout frequency recommendations"].
                  map((item) =>
                  <li key={item} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#FF6B35] shrink-0" />
                      <span className="text-[#F5F5F5] text-sm">{item}</span>
                    </li>
                  )}
                </ul>
              </ScrollReveal>

              <ScrollReveal delay={400}>
                <Link
                  to="/calculator"
                  className="btn-shimmer text-white font-semibold px-8 py-4 rounded-xl text-base tracking-wider uppercase flex items-center justify-center gap-2 hover:scale-105 transition-transform duration-200 w-fit animate-pulse-glow"
                  style={{ fontFamily: "var(--font-heading)" }}>

                  Calculate Now — It's Free
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </ScrollReveal>
            </div>

            {/* Right visual */}
            <div className="relative hidden lg:flex items-center justify-center p-8">
              <div className="relative w-full max-w-sm">
                {/* Mock calculator card */}
                <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-[#FF6B35]" />
                    <div className="w-3 h-3 rounded-full bg-[#FFA823]" />
                    <div className="w-3 h-3 rounded-full bg-[#2E86AB]" />
                  </div>

                  {/* Mock inputs */}
                  {[
                  { label: "Current Weight", value: "165 lbs" },
                  { label: "Height", value: "5'10\"" },
                  { label: "Body Fat %", value: "18%" },
                  { label: "Training Experience", value: "Intermediate" }].
                  map((field) =>
                  <div key={field.label} className="mb-4">
                      <label className="text-[#9CA3AF] text-xs mb-1.5 block">
                        {field.label}
                      </label>
                      <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F5]">
                        {field.value}
                      </div>
                    </div>
                  )}

                  {/* Mock result */}
                  <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-[#FF6B35]/10 to-[#FFA823]/10 border border-[#FF6B35]/20">
                    <div className="text-[#9CA3AF] text-xs mb-1">
                      Projected Lean Muscle Gain
                    </div>
                    <div
                      className="text-3xl font-bold gradient-text"
                      style={{ fontFamily: "var(--font-heading)" }}>

                      +8.5 LBS
                    </div>
                    <div className="text-[#9CA3AF] text-xs mt-1">
                      in 12 weeks with optimal training
                    </div>
                  </div>
                </div>

                {/* Decorative floating elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#FF6B35]/10 rounded-2xl blur-xl" />
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-[#2E86AB]/10 rounded-2xl blur-xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ─── About / Value Proposition Section ─── */
function AboutSection() {
  return (
    <section id="about" className="relative py-24 lg:py-32 bg-[#0A0A16]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left — Image collage */}
          <ScrollReveal animation="slide-left">
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden h-48">
                    <img
                      src={IMAGES.nutrition}
                      alt="Healthy meal prep"
                      className="w-full h-full object-cover" />

                  </div>
                  <div className="rounded-2xl overflow-hidden h-64">
                    <img
                      src={IMAGES.leanMan}
                      alt="Lean physique training"
                      className="w-full h-full object-cover" />

                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden h-64">
                    <img
                      src={IMAGES.womanStrength}
                      alt="Women strength training"
                      className="w-full h-full object-cover" />

                  </div>
                  <div className="rounded-2xl overflow-hidden h-48">
                    <img
                      src={IMAGES.ectomorph}
                      alt="Hardgainer transformation"
                      className="w-full h-full object-cover" />

                  </div>
                </div>
              </div>
              {/* Accent border */}
              <div className="absolute -bottom-3 -right-3 w-32 h-32 border-2 border-[#FF6B35]/20 rounded-2xl -z-10" />
            </div>
          </ScrollReveal>

          {/* Right — Content */}
          <ScrollReveal animation="slide-right">
            <span className="inline-block text-xs text-[#FF6B35] tracking-[0.3em] uppercase mb-3 font-semibold">
              Our Mission
            </span>
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#F5F5F5] mb-6 leading-tight"
              style={{ fontFamily: "var(--font-heading)" }}>

              SIMPLIFYING THE
              <br />
              <span className="gradient-text">SCIENCE OF HYPERTROPHY</span>
            </h2>
            <p className="text-[#9CA3AF] text-lg leading-relaxed mb-6">
              Building Mass Muscle exists to cut through the noise. We translate
              complex exercise science into actionable, free programs that
              deliver real results — whether you are a complete beginner, a
              woman looking to build functional strength, or a hardgainer who
              has struggled to put on size.
            </p>
            <p className="text-[#9CA3AF] text-base leading-relaxed mb-8">
              Every article, workout, and nutrition guide is rooted in
              peer-reviewed research and tested by real athletes. No
              supplements to sell. No shortcuts. Just the{" "}
              <strong className="text-[#F5F5F5]">
                quickest muscle to grow
              </strong>{" "}
              strategies and the{" "}
              <strong className="text-[#F5F5F5]">easiest muscle to build</strong>{" "}
              approaches that actually work.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {[
              {
                title: "Free Forever",
                desc: "Professional-grade programs at zero cost"
              },
              {
                title: "Science-Backed",
                desc: "Every recommendation is evidence-based"
              },
              {
                title: "All Body Types",
                desc: "Programs for ectomorphs to endomorphs"
              },
              {
                title: "Real Results",
                desc: "Proven transformations from our community"
              }].
              map((item) =>
              <div
                key={item.title}
                className="bg-white/3 border border-white/5 rounded-xl p-4">

                  <h4
                  className="text-[#F5F5F5] text-sm font-bold mb-1"
                  style={{ fontFamily: "var(--font-heading)" }}>

                    {item.title}
                  </h4>
                  <p className="text-[#9CA3AF] text-xs leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              )}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>);

}

/* ─── Nutrition Banner ─── */
function NutritionBanner() {
  return (
    <section className="relative py-20 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={IMAGES.nutrition}
          alt="Healthy nutrition for muscle building"
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-[#0F0F1A]/85" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal className="text-center">
          <span className="inline-block text-xs text-[#FFA823] tracking-[0.3em] uppercase mb-3 font-semibold">
            Fuel Your Gains
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F5F5F5] mb-5"
            style={{ fontFamily: "var(--font-heading)" }}>

            NUTRITION IS <span className="gradient-text">HALF THE BATTLE</span>
          </h2>
          <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto leading-relaxed mb-8">
            The best training program in the world is useless without proper
            nutrition. Our guides prioritize health and aesthetics — helping
            you fuel lean muscle growth without unnecessary fat gain.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
            "High-Protein Meal Plans",
            "Caloric Surplus Guides",
            "Supplement Science",
            "Hydration Protocols"].
            map((tag) =>
            <span
              key={tag}
              className="text-sm px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-[#F5F5F5]">

                {tag}
              </span>
            )}
          </div>
        </ScrollReveal>
      </div>
    </section>);

}

/* ─── Main Home Page ─── */
function Home() {
  useHead({
    title: PAGE_SEO.home.title,
    meta: [
      { name: "description", content: PAGE_SEO.home.description },
      { name: "keywords", content: PAGE_SEO.home.keywords },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE.name },
      { property: "og:title", content: PAGE_SEO.home.title },
      { property: "og:description", content: PAGE_SEO.home.description },
      { property: "og:image", content: PAGE_SEO.home.ogImage },
      { property: "og:url", content: buildCanonical(PAGE_SEO.home.canonicalPath) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: SITE.twitterHandle },
      { name: "twitter:title", content: PAGE_SEO.home.title },
      { name: "twitter:description", content: PAGE_SEO.home.description },
      { name: "twitter:image", content: PAGE_SEO.home.ogImage },
    ],
    link: [
      { rel: "canonical", href: buildCanonical(PAGE_SEO.home.canonicalPath) },
    ],
    script: [
      { type: "application/ld+json", innerHTML: buildWebsiteJsonLd() },
    ],
  });

  return (
    <div className="min-h-screen bg-[#0F0F1A]">
      <Navbar />
      <AffiliateBanner />
      <HeroSection />
      <MidAffiliateBanner />
      <div className="section-divider" />
      <TrainingHubsSection />
      <NutritionBanner />
      <ArticlesSection />
      <CalculatorCTA />
      <AboutSection />
      <Footer />
    </div>);

}

export default Home;